import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInYears,
    type Locale,
} from "date-fns";
import { enGB, es, fr } from "date-fns/locale";
import { useTranslation } from "react-i18next";

const JUST_NOW_THRESHOLD_IN_SECONDS = 60;

export const localeToDateFNSLocale: Record<string, Locale> = {
    en: enGB,
    fr,
    es,
};

/**
 * Formats in the language the application is read in. Left to the browser, a French interface
 * served to an American machine reads its dates month first and drops the century.
 */
export const formatShortDate = (value: string, language: string) =>
    new Intl.DateTimeFormat(language, { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(value));

/** What i18n falls back to, and what a date reads in before the reader has chosen a language. */
const FALLBACK_LANGUAGE = "fr";

export const useShortDate = () => {
    const [, i18n] = useTranslation();

    return (value: string) => formatShortDate(value, i18n?.language ?? FALLBACK_LANGUAGE);
};

type TimeSinceOptions = {
    now: Date;
    locale: string;
    justNowLabel: string;
};

const COARSER_UNITS: ReadonlyArray<{
    unit: Intl.RelativeTimeFormatUnit;
    elapsedSince: (now: Date, date: Date) => number;
}> = [
    { unit: "year", elapsedSince: differenceInYears },
    { unit: "month", elapsedSince: differenceInMonths },
    { unit: "day", elapsedSince: differenceInDays },
    { unit: "hour", elapsedSince: differenceInHours },
];

const toElapsedTime = (date: Date, now: Date) =>
    COARSER_UNITS.map(({ unit, elapsedSince }) => ({ unit, elapsed: elapsedSince(now, date) })).find(
        ({ elapsed }) => elapsed >= 1,
    ) ?? { unit: "minute" as const, elapsed: differenceInMinutes(now, date) };

/** An event younger than a minute reads as "just now" rather than as a duration. */
export const formatTimeSince = (date: Date, { now, locale, justNowLabel }: TimeSinceOptions): string => {
    if (differenceInSeconds(now, date) < JUST_NOW_THRESHOLD_IN_SECONDS) {
        return justNowLabel;
    }

    const { elapsed, unit } = toElapsedTime(date, now);

    return new Intl.RelativeTimeFormat(locale, { numeric: "always" }).format(-elapsed, unit);
};
